<script setup lang="ts">
  import { computed } from 'vue'
  import { slugify } from '../../../utils/string'
  import ErrorMessage from './partials/ErrorMessage.vue'
  import HelpMessage from './partials/HelpMessage.vue'
  import Label from './partials/Label.vue'
  import RequiredMark from './partials/RequiredMark.vue'
  import ValidationErrorIcon from './partials/ValidationErrorIcon.vue'

  export interface Props {
    id?: string
    type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url'
    name: string
    label?: string
    placeholder?: string
    validationErrors?: { [key: string]: [key: number] }
    required?: boolean
    modelValue?: string | number | null
    modelModifiers?: {
      prevent?: boolean
      uppercase?: boolean
    }
    help?: string
  }
  const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    required: false,
    modelValue: null,
  })

  const id = computed(() => {
    return props.id ?? slugify(`input-${props.type}-${props.name}`)
  })

  const placeholder = computed(() => {
    return props.placeholder ?? props.label
  })

  const errorMessage = computed(() => {
    if (!props.validationErrors?.[props.name]) {
      return null
    }

    return props.validationErrors[props.name][0]
  })

  const emit = defineEmits<{
    'update:modelValue': [value: string]
    keydown: [event: KeyboardEvent]
  }>()

  function emitInputValue(event: Event) {
    let value = (event.target as HTMLInputElement).value

    if (props.modelModifiers?.uppercase) {
      value = value.toUpperCase()
    }

    emit('update:modelValue', value)
  }

  function emitKeydown(event: KeyboardEvent) {
    if (props.modelModifiers?.prevent) {
      event.preventDefault()
    }

    emit('keydown', event)
  }
</script>

<template>
  <div class="text-left">
    <Label v-if="props.label" v-cloak :for="id">
      {{ props.label }}
    </Label>

    <div class="relative rounded-md" :class="{ 'mt-2': props.label }">
      <div
        v-if="$slots.prepend"
        class="absolute inset-y-0 start-0 flex w-9 items-center justify-center rounded-md rounded-r-none bg-wt-gray-lr text-sm text-wt-gray-d"
      >
        <slot name="prepend"></slot>
      </div>

      <input
        @keydown="emitKeydown"
        @input="emitInputValue"
        class="w-full rounded-md border border-wt-gray-l px-3 py-1.5 text-sm placeholder:text-wt-gray-l focus:ring-3 focus:!outline-hidden disabled:cursor-not-allowed disabled:bg-wt-gray-lr disabled:text-wt-gray-d"
        :id="id"
        :class="{
          'ps-11': $slots.prepend,
          'pe-11': $slots.append,
          '!border-wt-red-l !pe-10 !text-wt-red-dr focus:!border-wt-red-l focus:!ring-wt-red-lr/50':
            errorMessage,
          'border-wt-gray-l focus:border-wt-blue-l focus:ring-wt-blue-lr/50':
            !errorMessage,
        }"
        :value="modelValue"
        :type="props.type"
        :name="props.name"
        :placeholder="placeholder"
      />

      <RequiredMark v-if="props.required" v-cloak />

      <div
        v-if="$slots.append"
        class="absolute inset-y-0 end-0 flex w-9 items-center justify-center rounded-md rounded-l-none text-sm text-wt-gray-d"
      >
        <slot name="append"></slot>
      </div>

      <ValidationErrorIcon v-if="errorMessage" v-cloak />
    </div>

    <ErrorMessage v-if="errorMessage" v-cloak :id="`${id}-error-message`">
      {{ errorMessage }}
    </ErrorMessage>

    <HelpMessage v-if="help" v-cloak>
      {{ help }}
    </HelpMessage>
  </div>
</template>
